import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, MapPin, CalendarDays, Home, Users } from 'lucide-react';
import { TID, LOCATIONS, PROPERTY_TYPES, PLATFORM_MAX_GUESTS } from '@yalidavet/shared';
import { todayIso, addDaysIso, cn } from '@/lib/utils';

export interface HeroSearchValue {
  city?: string;
  district?: string;
  checkin?: string;
  checkout?: string;
  propertyType?: string;
  guestCount?: number;
}

interface Props {
  initial?: HeroSearchValue;
  /** "Ara" on the landing page, "Güncelle" on the sticky search bar. */
  submitLabel?: string;
  compact?: boolean;
  onSubmit?: (value: HeroSearchValue) => void;
  testId?: string;
  submitTestId?: string;
}

export default function HeroSearch({
  initial,
  submitLabel = 'Ara',
  compact = false,
  onSubmit,
  testId = TID.heroSearch,
  submitTestId = TID.searchSubmit,
}: Props) {
  const navigate = useNavigate();
  const [value, setValue] = useState<HeroSearchValue>({
    district: initial?.district ?? '',
    checkin: initial?.checkin ?? '',
    checkout: initial?.checkout ?? '',
    propertyType: initial?.propertyType ?? '',
    guestCount: initial?.guestCount,
  });

  const set = <K extends keyof HeroSearchValue>(key: K, v: HeroSearchValue[K]) =>
    setValue((prev) => {
      const next = { ...prev, [key]: v };
      // Keep checkout strictly after checkin.
      if (key === 'checkin' && next.checkout && next.checkout <= String(v)) {
        next.checkout = addDaysIso(String(v), 1);
      }
      return next;
    });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (onSubmit) {
      onSubmit(value);
      return;
    }
    const params = new URLSearchParams();
    if (value.district) params.set('district', value.district);
    if (value.checkin) params.set('checkin', value.checkin);
    if (value.checkout) params.set('checkout', value.checkout);
    if (value.propertyType) params.set('propertyType', value.propertyType);
    if (value.guestCount) params.set('guestCount', String(value.guestCount));
    navigate(`/ara?${params.toString()}`);
  };

  return (
    <form
      onSubmit={handleSubmit}
      data-testid={testId}
      className={cn(
        'grid w-full gap-3 rounded-2xl border-2 border-white/30 bg-white/95 backdrop-blur-md p-4 shadow-2xl ring-1 ring-gold-300/30 transition-all duration-300',
        compact ? 'md:grid-cols-[1fr_1fr_1fr_1fr_auto]' : 'md:grid-cols-[1.2fr_1fr_1fr_1fr_.8fr_auto]',
      )}
    >
      {/* Lokasyon */}
      <label className="flex items-center gap-2 rounded-lg px-3 py-2.5 hover:bg-burgundy-50 transition-colors duration-200 cursor-pointer group">
        <MapPin className="h-5 w-5 shrink-0 text-burgundy-600 group-hover:text-burgundy-700" aria-hidden />
        <span className="sr-only">Lokasyon</span>
        <select
          data-testid={TID.searchLocation}
          value={value.district}
          onChange={(e) => set('district', e.target.value)}
          className="w-full bg-transparent text-sm font-medium text-burgundy-900 outline-none placeholder:text-yali-400"
        >
          <option value="">Lokasyon</option>
          {LOCATIONS.map((l) => (
            <option key={l.value} value={l.value}>{l.label}</option>
          ))}
        </select>
      </label>

      {/* Tarih: giriş */}
      <label className="flex items-center gap-2 rounded-lg px-3 py-2.5 hover:bg-burgundy-50 transition-colors duration-200 cursor-pointer group" data-testid={TID.searchDate}>
        <CalendarDays className="h-5 w-5 shrink-0 text-burgundy-600 group-hover:text-burgundy-700" aria-hidden />
        <span className="sr-only">Giriş tarihi</span>
        <input
          type="date"
          data-testid={TID.searchCheckin}
          min={todayIso()}
          value={value.checkin}
          onChange={(e) => set('checkin', e.target.value)}
          className="w-full bg-transparent text-sm font-medium text-burgundy-900 outline-none"
          aria-label="Giriş tarihi"
        />
      </label>

      {/* Tarih: çıkış */}
      <label className="flex items-center gap-2 rounded-lg px-3 py-2.5 hover:bg-burgundy-50 transition-colors duration-200 cursor-pointer group">
        <CalendarDays className="h-5 w-5 shrink-0 text-burgundy-600 group-hover:text-burgundy-700" aria-hidden />
        <span className="sr-only">Çıkış tarihi</span>
        <input
          type="date"
          data-testid={TID.searchCheckout}
          min={value.checkin ? addDaysIso(value.checkin, 1) : todayIso()}
          value={value.checkout}
          onChange={(e) => set('checkout', e.target.value)}
          className="w-full bg-transparent text-sm font-medium text-burgundy-900 outline-none"
          aria-label="Çıkış tarihi"
        />
      </label>

      {/* Mülk tipi */}
      <label className="flex items-center gap-2 rounded-lg px-3 py-2.5 hover:bg-burgundy-50 transition-colors duration-200 cursor-pointer group">
        <Home className="h-5 w-5 shrink-0 text-burgundy-600 group-hover:text-burgundy-700" aria-hidden />
        <span className="sr-only">Mülk tipi</span>
        <select
          data-testid={TID.searchPropertyType}
          value={value.propertyType}
          onChange={(e) => set('propertyType', e.target.value)}
          className="w-full bg-transparent text-sm font-medium text-burgundy-900 outline-none"
        >
          <option value="">Mülk Tipi</option>
          {PROPERTY_TYPES.map((p) => (
            <option key={p.value} value={p.value}>{p.label}</option>
          ))}
        </select>
      </label>

      {/* Misafir sayısı — drives the garden-size tier */}
      {!compact && (
        <label className="flex items-center gap-2 rounded-lg px-3 py-2.5 hover:bg-burgundy-50 transition-colors duration-200 cursor-pointer group">
          <Users className="h-5 w-5 shrink-0 text-burgundy-600 group-hover:text-burgundy-700" aria-hidden />
          <span className="sr-only">Misafir sayısı</span>
          <input
            type="number"
            inputMode="numeric"
            min={1}
            max={PLATFORM_MAX_GUESTS}
            placeholder="Misafir"
            data-testid={TID.searchGuests}
            value={value.guestCount ?? ''}
            onChange={(e) => set('guestCount', e.target.value ? Number(e.target.value) : undefined)}
            className="w-full bg-transparent text-sm font-medium text-burgundy-900 outline-none placeholder:text-yali-400"
          />
        </label>
      )}

      <button type="submit" className="yd-btn-primary md:px-6 font-semibold shadow-burgundy hover:shadow-gold" data-testid={submitTestId}>
        <Search className="h-5 w-5" aria-hidden />
        {submitLabel}
      </button>
    </form>
  );
}
